import {
  FlowBlockType,
  type FlowDuration,
  FlowDurationSchema,
  type FlowDurationUnits,
  FlowObsoleteBlockType,
} from 'flow-schema';
import { z } from 'zod';

export enum UnknownBlockType {
  /**
   * This is the only unknown block type allowed.
   * It handles cases where the API says there is a block, but the type is not known.
   * This can happen during upgrades or when the API is out of sync.
   */
  Unknown = 'Unknown',
}

export const SingletonBlockTypes = new Set<FlowBlockType>([
  FlowBlockType.Start,
  FlowBlockType.Output,
]);

/**
 * Skills cannot be picked directly as a block. They are hydrated from the skill API data when
 * picked, though an existing block can still be copied and pasted with its skill data.
 */
export const NonPickableBlockTypes = new Set<FlowBlockType>([FlowBlockType.SkillV2]);

export const AnyBlockTypeSchema = z
  .enum(FlowBlockType)
  .or(z.enum(FlowObsoleteBlockType))
  .or(z.enum(UnknownBlockType));
export type AnyBlockType = z.infer<typeof AnyBlockTypeSchema>;

export enum RunMode {
  Live = 'LIVE',
  Test = 'TEST',
}

/**
 * TODO: Convert FlowBlocks to schemas to allow us to remove this duplicate interface
 */
/**
 * Note - this does not account for DST transitions, or leap years.
 * Regarding DST transitions:
 * 1 day in milliseconds is not the same for a DST transition.
 * 1 day for the switch from standard time to DST is 23 hours.
 * 1 day for the switch from DST to standard time is 25 hours.
 * If you need to account for correct date durations, use date-fns Date math utilities,
 * such as https://date-fns.org/v4.1.0/docs/add
 */
export function getDurationInMillis(value: number, unit: FlowDurationUnits): number {
  const durations: { [key in FlowDurationUnits]: number } = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions)
    weeks: 7 * 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions)
    years: 365 * 24 * 60 * 60 * 1000, // Approximate (Not valid for leap years)
  };
  return value * durations[unit];
}

export const DurationSchemaWithMax = (pathName: string, max: FlowDuration) => {
  const maxMillis = getDurationInMillis(max.value, max.units);
  return FlowDurationSchema.refine(
    (arg: FlowDuration) => {
      const millis = getDurationInMillis(arg.value, arg.units);
      return millis <= maxMillis;
    },
    {
      message: `Max ${pathName} is ${max.value} ${max.units}`,
      path: [pathName],
    }
  );
};

export function StringAsNumberSchema(
  numberValidation: z.ZodType<number | undefined, number | undefined>
) {
  return z.coerce
    .string()
    .optional()
    .transform((value) => (value === '' ? undefined : value))
    .refine((value) => value === undefined || !isNaN(Number(value)), {
      message: 'Invalid number',
    })
    .transform((value) => (value === undefined ? undefined : Number(value)))
    .pipe(numberValidation) as unknown as z.ZodOptional<z.ZodType<number>>;
}
